import React, { useEffect, useState, useCallback } from 'react';
import api from '../utils/api';
import { useToast } from '../context/ToastContext';

const fmtN = (n,dp=0) => Number(n||0).toLocaleString(undefined,{maximumFractionDigits:dp});

export default function TiepGoodsPage() {
  const toast = useToast();
  const [goods, setGoods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.get('/tiep_goods/list.php').then(r => { if(r.data.success) setGoods(r.data.data); }).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async id => {
    if (!window.confirm('Delete this TIEP entry?')) return;
    const { data } = await api.post('/tiep_goods/delete.php', { id });
    if (data.success) { toast('Deleted'); load(); } else toast(data.error||'Delete failed', 'error');
  };

  const filtered = goods.filter(g => !search || [g.name,g.cusdec_no,g.hs_code,g.goods].some(v => v?.toLowerCase().includes(search.toLowerCase())));

  const getStatus = t => {
    const pct = t.qty ? (t.issued_qty/t.qty)*100 : 0;
    if (pct>=100) return { label:'Exhausted', cls:'badge-red',   pct:100, bar:'#ef4444' };
    if (pct>70)   return { label:'Low Stock',  cls:'badge-amber', pct,     bar:'#f59e0b' };
    return            { label:'Active',      cls:'badge-green', pct,     bar:'var(--teal)' };
  };

  const totalLiab    = goods.reduce((a,g) => a + +g.liability, 0);
  const totalBal     = goods.reduce((a,g) => a + +(g.balance||0), 0);
  const totalBalLiab = goods.reduce((a,g) => {
    const perUnit = g.qty ? +g.liability / +g.qty : 0;
    return a + perUnit * +(g.balance||0);
  }, 0);

  return (
    <div>
      <div className="page-header">
        <div><div className="page-title">TIEP Goods Register</div><div className="page-sub">Full stock register with balance & liability tracking</div></div>
        <div style={{ display:'flex', gap:10 }}>
          <input className="input" style={{ width:220 }} placeholder="Search supplier, CUSDEC…" value={search} onChange={e => setSearch(e.target.value)} />
          <button className="btn btn-secondary" onClick={load}>↻ Refresh</button>
        </div>
      </div>

      <div className="page-body" style={{ display:'flex', flexDirection:'column', gap:16 }}>
        {/* Summary */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12 }}>
          {[
            { label:'Total Entries',          val:goods.length },
            { label:'Total Liability (LKR)',  val:fmtN(totalLiab) },
            { label:'Balance Units',          val:fmtN(totalBal) },
            { label:'Balance Liability (LKR)',val:fmtN(totalBalLiab), accent:true },
            { label:'Filtered Results',       val:filtered.length },
          ].map(s => (
            <div key={s.label} style={{ background: s.accent ? 'var(--teal)' : '#fff', border:'1px solid var(--border)', borderRadius:10, padding:'12px 16px' }}>
              <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:.5, color: s.accent ? 'rgba(255,255,255,.7)' : 'var(--text2)' }}>{s.label}</div>
              <div style={{ fontSize:20, fontWeight:700, marginTop:3, color: s.accent ? '#fff' : 'var(--navy)' }}>{s.val}</div>
            </div>
          ))}
        </div>

        <div className="card">
          <div className="tbl-wrap">
            <table>
              <thead>
                <tr>{['Date','Supplier','Goods','CUSDEC','HS Code','QTY','Units','New Wt','Value LKR','Liability LKR','Issued','Balance QTY','Balance Liability','% Used',''].map(h=><th key={h}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {loading
                  ? <tr><td colSpan={15} style={{ textAlign:'center', padding:30, color:'var(--text2)' }}>Loading…</td></tr>
                  : filtered.length===0
                  ? <tr><td colSpan={15} style={{ textAlign:'center', padding:30, color:'var(--text2)' }}>No TIEP goods found.</td></tr>
                  : filtered.map(t => {
                    const s = getStatus(t);
                    const bal = +(t.balance||0);
                    const perUnit = t.qty ? +t.liability / +t.qty : 0;
                    const balLiab = perUnit * bal;
                    return (
                      <tr key={t.id}>
                        <td>{new Date(t.date).toLocaleDateString('en-GB')}</td>
                        <td><strong>{t.name}</strong><br/><span style={{ fontSize:11, color:'var(--text2)' }}>{t.goods}</span></td>
                        <td style={{ maxWidth:120 }}>{t.goods}</td>
                        <td className="mono">{t.cusdec_no}</td>
                        <td className="mono">{t.hs_code}</td>
                        <td>{fmtN(t.qty)}</td>
                        <td>{t.units}</td>
                        <td>{fmtN(t.new_wt)}</td>
                        <td>{fmtN(t.value_usd)}</td>
                        <td>{fmtN(t.liability)}</td>
                        <td style={{ color:'var(--text2)' }}>{fmtN(t.issued_qty||0)}</td>
                        <td><strong style={{ color: bal<=0 ? 'var(--danger)' : 'var(--teal)' }}>{fmtN(bal)}</strong></td>
                        <td><strong style={{ color: bal<=0 ? 'var(--danger)' : 'var(--gold)' }}>{fmtN(balLiab)}</strong></td>
                        <td style={{ minWidth:100 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <div className="prog" style={{ flex:1 }}>
                              <div className="prog-fill" style={{ width:`${Math.min(s.pct,100)}%`, background:s.bar }} />
                            </div>
                            <span style={{ fontSize:11, color:'var(--text2)', minWidth:30 }}>{Math.round(s.pct)}%</span>
                          </div>
                          <span className={`badge ${s.cls}`} style={{ marginTop:4, display:'inline-flex' }}>{s.label}</span>
                        </td>
                        <td>
                          <button className="btn-icon danger" onClick={() => handleDelete(t.id)} title="Delete">🗑</button>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <style>{`@media(max-width:900px){div[style*="repeat(5,1fr)"]{grid-template-columns:repeat(2,1fr)!important}}`}</style>
    </div>
  );
}
